/**
 * Shared HPO types used across the app (search results, form options, etc.).
 * API returns code/description; we map to this shape where a "node" is needed.
 */

export interface HPOMeta {
  definition?: { val: string; xrefs?: string[] }
  comments?: string[]
  synonyms?: Array<{ pred: string; val: string; synonymType?: string }>
  xrefs?: Array<{ val: string }>
  basicPropertyValues?: Array<{ pred: string; val: string }>
}

export interface HPONode {
  id: string
  lbl: string
  type?: string
  meta?: HPOMeta
}

/**
 * True when the value is itself an HPO code (e.g. "HP:0001250" or "HP_0001250"),
 * not a human-readable label. Used to detect legacy canonical rows that need
 * async resolution against the HPO terminology service.
 */
export function isHpoCode(value: string | null | undefined): boolean {
  return Boolean(value && /^HP[:_]\d+$/i.test(value.trim()))
}

/**
 * Normalize HPO id to HP:XXXXXXXX form.
 * Converts "http://purl.obolibrary.org/obo/HP_0000001" or "HP_0000001" to "HP:0000001".
 */
export function extractHPOId(urlOrId: string): string {
  if (!urlOrId) return urlOrId
  const trimmed = urlOrId.trim()
  if (/^HP:\d+$/i.test(trimmed)) return trimmed
  const match = trimmed.match(/HP_(\d+)/i)
  if (match) return `HP:${match[1]}`
  const colonMatch = trimmed.match(/HP:(\d+)/i)
  if (colonMatch) return colonMatch[0]
  return trimmed
}
